import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Building2, LockKeyhole, Mail, MonitorPlay, ShieldCheck, Sparkles, Zap } from 'lucide-react';
import { sendPasswordResetEmail, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import LoadingScreen from '../../components/LoadingScreen';

export default function LoginPage({ portal = 'tenant' }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [platformBrand, setPlatformBrand] = useState({ logoUrl: '', platformName: 'Weilink Signage' });
  const { authUser, profile, loading, isSuperAdmin } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  const superPortal = portal === 'super';

  useEffect(() => {
    let alive = true;
    getDoc(doc(db, 'system_settings', 'config'))
      .then(snapshot => {
        if (!alive || !snapshot.exists()) return;
        const data = snapshot.data();
        setPlatformBrand({
          logoUrl: data.logoUrl || '',
          platformName: data.platformName || 'Weilink Signage'
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (loading) return <LoadingScreen />;
  if (authUser && profile) {
    return <Navigate to={isSuperAdmin ? '/superadmin/dashboard' : '/tenant/dashboard'} replace />;
  }

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
      const profileSnap = await getDoc(doc(db, 'users', credential.user.uid));
      const signedProfile = profileSnap.exists() ? profileSnap.data() : null;
      const signedAsSuper = signedProfile?.role === 'super_admin' || signedProfile?.tenantId === 'super_admin';

      if (superPortal && !signedAsSuper) {
        await signOut(auth);
        notify('This account is not a Super Admin. Use the tenant login URL.', 'error');
        return;
      }
      if (!superPortal && signedAsSuper) {
        await signOut(auth);
        notify('Super Admin accounts must use the Super Admin login URL.', 'error');
        return;
      }

      navigate(superPortal ? '/superadmin/dashboard' : '/tenant/dashboard', { replace: true });
    } catch (error) {
      notify(String(error?.message || error).replace('Firebase:', '').trim(), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    if (!email.trim()) { notify('Enter your email first.', 'info'); return; }
    try {
      await sendPasswordResetEmail(auth, email.trim());
      notify('Password reset email sent.', 'success');
    } catch (error) {
      notify(error.message, 'error');
    }
  }

  return (
    <div className="auth-page">
      <section className={`auth-visual ${superPortal ? 'super-auth-visual' : ''}`}>
        <div className="auth-brand">
          <div className={`brand-mark auth-brand-logo ${platformBrand.logoUrl ? 'has-logo' : ''}`}>
            {platformBrand.logoUrl ? <img src={platformBrand.logoUrl} alt={`${platformBrand.platformName} logo`} /> : <Zap size={20} fill="currentColor" />}
          </div>
          <div className="auth-brand-copy"><span>{platformBrand.platformName}</span><small>{superPortal ? 'Platform Control Center' : 'Cloud Signage Workspace'}</small></div>
        </div>
        <div className="auth-hero">
          <span className="eyebrow">{superPortal ? 'Platform administration' : 'Cloud digital signage'}</span>
          <h1>{superPortal ? 'Manage companies, subscriptions and the entire signage platform.' : 'Control every screen from one responsive workspace.'}</h1>
          <p>{superPortal ? 'Create companies, define packages, approve subscriptions, suspend accounts and monitor global screen usage.' : 'Publish media, schedule campaigns, monitor devices and manage your subscription from one console.'}</p>
          <div className="auth-feature-grid">
            <div>{superPortal ? <Building2 /> : <MonitorPlay />}<strong>{superPortal ? 'Company control' : 'Remote screens'}</strong><span>{superPortal ? 'Tenants, owners and status.' : 'Pair, monitor and control devices.'}</span></div>
            <div>{superPortal ? <ShieldCheck /> : <Sparkles />}<strong>{superPortal ? 'Subscription authority' : 'Smart content'}</strong><span>{superPortal ? 'Plans, approvals and limits.' : 'Playlists, layouts and widgets.'}</span></div>
          </div>
        </div>
        <div className="auth-glow auth-glow-a" /><div className="auth-glow auth-glow-b" />
      </section>

      <section className="auth-form-wrap">
        <form className="auth-card" onSubmit={submit}>
          <div className="login-logo-panel">
            <div className={`login-logo ${platformBrand.logoUrl ? 'has-image' : ''}`}>
              {platformBrand.logoUrl ? <img src={platformBrand.logoUrl} alt={`${platformBrand.platformName} logo`} /> : <Zap size={25} fill="currentColor" />}
            </div>
            <div><strong>{platformBrand.platformName}</strong><span>{superPortal ? 'Super Admin Portal' : 'Tenant Administration'}</span></div>
          </div>
          <div className="auth-card-head">
            <span className="eyebrow">{superPortal ? 'Super Admin Portal' : 'Tenant Portal'}</span>
            <h2>{superPortal ? 'Super Admin sign in' : 'Company sign in'}</h2>
            <p>{superPortal ? 'Only platform-level Super Admin credentials are accepted here.' : 'Use your company owner/admin credentials.'}</p>
          </div>
          <label className="field"><span>Email address</span><div className="input-icon"><Mail size={17} /><input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={superPortal ? 'admin@platform.com' : 'you@company.com'} required /></div></label>
          <label className="field"><span>Password</span><div className="input-icon"><LockKeyhole size={17} /><input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required /></div></label>
          <div className="form-row-between"><span /><button type="button" className="link-btn" onClick={resetPassword}>Forgot password?</button></div>
          <button className="btn btn-primary btn-wide" disabled={busy}>{busy ? 'Signing in…' : superPortal ? 'Sign in as Super Admin' : 'Sign in'}</button>

          <div className="auth-divider"><span>{superPortal ? 'Company administrator?' : 'Platform administrator?'}</span></div>
          <Link className="btn btn-soft btn-wide" to={superPortal ? '/tenant/login' : '/superadmin/login'}>{superPortal ? 'Open Tenant Login' : 'Open Super Admin Login'}</Link>
          {!superPortal && <Link className="auth-secondary-link" to="/tenant/register">New customer? Create account & choose a plan</Link>}
        </form>
      </section>
    </div>
  );
}
